/**
 * SpeakerRoleAssignmentModal Component
 * ORCA360 - Speaker Role Assignment Modal
 *
 * Modal for assigning speaker roles after transcription (roles filtered by session scenario)
 * Critical for ensuring pilot assessments extract evidence from the correct pilot's speech
 *
 * Phase 2: Speaker Role Mapping, Day 1
 */

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAudioSnippetPlayer } from '../hooks/useAudioSnippetPlayer';
import { AudioSnippetPlayer } from './AudioSnippetPlayer';
import { LiveTranscriptScroller } from './LiveTranscriptScroller';
import type { SpeakerSnippet } from './AudioSnippetPlayer';
import type { TranscriptSegment } from './LiveTranscriptScroller';

export interface SpeakerInfo {
  speaker_label: string;
  segment_count: number;
  first_appearance: string; // timestamp
  sample_text: string;
  snippets: SpeakerSnippet[];
}

/** All valid speaker role values */
export type SpeakerRoleValue =
  | 'instructor' | 'captain' | 'captain_2' | 'first_officer' | 'first_officer_2'
  | 'trainee' | 'examiner' | 'check_airman' | 'losa_observer' | 'observer'
  | 'other' | 'unknown';

export interface SpeakerRoleAssignment {
  speaker_label: string;
  speaker_role: SpeakerRoleValue;
  user_id?: string;
}

export interface SpeakerRoleAssignmentModalProps {
  sessionId: string;
  instructorId?: string;
  captainId?: string;
  firstOfficerId?: string;
  isOpen: boolean;
  onClose: () => void;
  onComplete: (assignments: SpeakerRoleAssignment[]) => void;
}

/** Recognized role labels that may appear as speaker_label from diarization/transcription */
const KNOWN_ROLE_LABELS: Record<string, string> = {
  instructor: 'Instructor',
  captain: 'Captain',
  captain_2: 'Captain 2',
  'first officer': 'First Officer',
  'first_officer': 'First Officer',
  'first_officer_2': 'First Officer 2',
  trainee: 'Trainee',
  examiner: 'Examiner',
  'check airman': 'Check Airman',
  check_airman: 'Check Airman',
  'losa observer': 'LOSA Observer',
  losa_observer: 'LOSA Observer',
  observer: 'Observer',
  unknown: 'Unknown',
};

/** Full set of role options with display metadata */
const ALL_ROLE_OPTIONS: { value: SpeakerRoleValue; label: string; shortLabel: string; activeColor: string; badgeColor: string }[] = [
  { value: 'instructor',    label: 'Instructor',    shortLabel: 'INSTR', activeColor: 'bg-blue-500 text-white',   badgeColor: 'bg-blue-100 text-blue-800 border-blue-300' },
  { value: 'captain',         label: 'Captain',         shortLabel: 'CAPT',   activeColor: 'bg-purple-500 text-white', badgeColor: 'bg-purple-100 text-purple-800 border-purple-300' },
  { value: 'captain_2',       label: 'Captain 2',       shortLabel: 'CAPT2',  activeColor: 'bg-purple-400 text-white', badgeColor: 'bg-purple-50 text-purple-700 border-purple-200' },
  { value: 'first_officer',   label: 'First Officer',   shortLabel: 'F/O',    activeColor: 'bg-green-500 text-white',  badgeColor: 'bg-green-100 text-green-800 border-green-300' },
  { value: 'first_officer_2', label: 'First Officer 2', shortLabel: 'F/O 2',  activeColor: 'bg-green-400 text-white',  badgeColor: 'bg-green-50 text-green-700 border-green-200' },
  { value: 'trainee',       label: 'Trainee',       shortLabel: 'TRN',   activeColor: 'bg-orange-500 text-white', badgeColor: 'bg-orange-100 text-orange-800 border-orange-300' },
  { value: 'examiner',      label: 'Examiner',      shortLabel: 'EXAM',  activeColor: 'bg-red-500 text-white',    badgeColor: 'bg-red-100 text-red-800 border-red-300' },
  { value: 'check_airman',  label: 'Check Airman',  shortLabel: 'CHK',   activeColor: 'bg-red-700 text-white',    badgeColor: 'bg-red-100 text-red-800 border-red-300' },
  { value: 'losa_observer', label: 'LOSA Observer', shortLabel: 'LOSA',  activeColor: 'bg-violet-500 text-white', badgeColor: 'bg-violet-100 text-violet-800 border-violet-300' },
  { value: 'observer',      label: 'Observer',      shortLabel: 'OBS',   activeColor: 'bg-slate-500 text-white',  badgeColor: 'bg-slate-100 text-slate-800 border-slate-300' },
  { value: 'other',         label: 'Other',         shortLabel: 'OTH',   activeColor: 'bg-gray-500 text-white',   badgeColor: 'bg-gray-100 text-gray-800 border-gray-300' },
  { value: 'unknown',       label: 'Unknown',       shortLabel: '?',     activeColor: 'bg-yellow-500 text-white', badgeColor: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
];

/** Map scenario speaker_roles display name → lowercase role value */
function mapScenarioRoleToValue(displayRole: string): SpeakerRoleValue | null {
  const map: Record<string, SpeakerRoleValue> = {
    'instructor': 'instructor',
    'captain': 'captain',
    'captain 2': 'captain_2',
    'first officer': 'first_officer',
    'first officer 2': 'first_officer_2',
    'trainee': 'trainee',
    'examiner': 'examiner',
    'check airman': 'check_airman',
    'losa observer': 'losa_observer',
    'observer': 'observer',
  };
  return map[displayRole.toLowerCase()] ?? null;
}

/**
 * Infer a role from a raw speaker_label string.
 * Handles labels like "Instructor - 2", "Student Pilot", "Captain (PF)", etc.
 * Returns a valid role or null if no match.
 */
function inferRoleFromLabel(rawLabel: string): SpeakerRoleValue | null {
  const lower = rawLabel.toLowerCase().trim();

  if (/\binstr/i.test(lower)) return 'instructor';
  if (/\b(check\s*airman)\b/i.test(lower)) return 'check_airman';
  if (/\blosa\s*observer\b/i.test(lower)) return 'losa_observer';
  if (/\bexaminer\b/i.test(lower)) return 'examiner';
  // Numbered variants must be checked before base roles
  if (/\b(captain|capt)\s*2\b/i.test(lower)) return 'captain_2';
  if (/\b(captain|capt)\b/i.test(lower)) return 'captain';
  if (/\b(first.?officer|f\/o|copilot|co-pilot)\s*2\b/i.test(lower)) return 'first_officer_2';
  if (/\b(first.?officer|f\/o|copilot|co-pilot)\b/i.test(lower)) return 'first_officer';
  if (/\b(student|trainee|cadet)\b/i.test(lower)) return 'trainee';
  if (/\bobserver\b/i.test(lower)) return 'observer';

  return null;
}

/**
 * Normalize a raw speaker_label for display.
 * If it matches a known role, return that role name.
 * If it already looks like "Speaker N" / "SPEAKER_N", keep it clean.
 * Otherwise, assign a generic "Speaker N" label based on its order.
 */
function getDisplayLabel(rawLabel: string, index: number): string {
  const lower = rawLabel.toLowerCase().trim();

  // Check for known role names (exact or prefix match like "instructor - 2")
  for (const [key, display] of Object.entries(KNOWN_ROLE_LABELS)) {
    if (lower === key) return display;
  }

  // Already a clean "Speaker N" or "SPEAKER_N" pattern
  if (/^speaker[\s_-]*\d+$/i.test(rawLabel.trim())) {
    const num = rawLabel.replace(/\D/g, '');
    return `Speaker ${num}`;
  }

  // Fallback: generic numbered label
  return `Speaker ${index + 1}`;
}

export function SpeakerRoleAssignmentModal({
  sessionId,
  instructorId,
  captainId,
  firstOfficerId,
  isOpen,
  onClose,
  onComplete,
}: SpeakerRoleAssignmentModalProps) {
  const [speakers, setSpeakers] = useState<SpeakerInfo[]>([]);
  const [assignments, setAssignments] = useState<Record<string, SpeakerRoleValue>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [allSegments, setAllSegments] = useState<TranscriptSegment[]>([]);
  const [scenarioRoles, setScenarioRoles] = useState<SpeakerRoleValue[]>([]);

  const { state: playbackState, playSnippet, pause, resume, stop, seekWithinSnippet } = useAudioSnippetPlayer({ audioUrl });

  // Load unique speakers from transcript
  useEffect(() => {
    if (isOpen && sessionId) {
      loadSpeakers();
    }
  }, [isOpen, sessionId]);

  const loadSpeakers = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch scenario speaker_roles for this session (for role filtering)
      const { data: sessionData } = await supabase
        .from('sessions')
        .select('transcription_scenario_id')
        .eq('id', sessionId)
        .single();

      if (sessionData?.transcription_scenario_id) {
        const { data: scenario } = await supabase
          .from('transcription_scenarios')
          .select('speaker_roles')
          .eq('id', sessionData.transcription_scenario_id)
          .single();

        if (scenario?.speaker_roles) {
          const mapped = scenario.speaker_roles
            .map((r: string) => mapScenarioRoleToValue(r))
            .filter((v: SpeakerRoleValue | null): v is SpeakerRoleValue => v !== null);
          setScenarioRoles(mapped);
        }
      }

      // Get transcript IDs for this session
      const { data: transcripts, error: transcriptError } = await supabase
        .from('transcripts')
        .select('id')
        .eq('session_id', sessionId);

      if (transcriptError) {
        throw new Error(`Failed to load transcripts: ${transcriptError.message}`);
      }

      if (!transcripts || transcripts.length === 0) {
        throw new Error('No transcripts found for this session');
      }

      const transcriptIds = transcripts.map((t) => t.id);

      // Fetch speaker segments and audio file URL in parallel
      const [segmentsResult, audioUrlResult] = await Promise.all([
        supabase
          .from('speaker_segments')
          .select('id, speaker_label, start_time, end_time, start_ms, end_ms, text')
          .in('transcript_id', transcriptIds)
          .order('start_time', { ascending: true }),
        (async () => {
          try {
            // 1. Try desktop-uploaded audio from files table
            const { data: audioFiles } = await supabase
              .from('files')
              .select('id, storage_path, file_name')
              .eq('session_id', sessionId)
              .eq('file_type', 'audio')
              .order('created_at', { ascending: false })
              .limit(1);

            const audioFile = audioFiles?.[0];
            if (audioFile?.storage_path) {
              const { data: urlData } = await supabase.storage
                .from('session-files')
                .createSignedUrl(audioFile.storage_path, 7200);
              if (urlData?.signedUrl) return urlData.signedUrl;
            }

            // 2. Fallback: try wizard recordings from session_recordings table
            // Prefer concatenated recordings (single files with timestamps matching speaker segments)
            const { data: recordings } = await supabase
              .from('session_recordings')
              .select('storage_path, phase, status')
              .eq('session_id', sessionId)
              .in('status', ['uploaded', 'transcribing', 'transcribed', 'completed'])
              .order('created_at', { ascending: false })
              .limit(5);

            // Find first recording with a file extension (skip directory-based mobile chunk paths)
            const fileRec = recordings?.find(
              (r) => /\.(mp3|m4a|webm|wav|ogg|mp4|aac)$/i.test(r.storage_path),
            );
            if (fileRec?.storage_path) {
              const { data: urlData } = await supabase.storage
                .from('session-recordings')
                .createSignedUrl(fileRec.storage_path, 7200);
              if (urlData?.signedUrl) return urlData.signedUrl;
            }

            return null;
          } catch {
            console.warn('[SpeakerRoleAssignmentModal] Could not load audio URL');
            return null;
          }
        })(),
      ]);

      const { data: segments, error: segmentsError } = segmentsResult;

      if (segmentsError) {
        throw new Error(`Failed to load speaker segments: ${segmentsError.message}`);
      }

      if (!segments || segments.length === 0) {
        throw new Error('No speaker segments found. Transcription may not be complete.');
      }

      // Set audio URL for playback
      setAudioUrl(audioUrlResult);

      // Store all segments for the live transcript scroller
      setAllSegments(segments as TranscriptSegment[]);

      // Group by speaker and collect statistics + snippets
      const speakerMap = new Map<string, SpeakerInfo>();
      const speakerSegments = new Map<string, typeof segments>();

      for (const segment of segments) {
        const label = segment.speaker_label;
        if (!speakerMap.has(label)) {
          speakerMap.set(label, {
            speaker_label: label,
            segment_count: 0,
            first_appearance: segment.start_time,
            sample_text: segment.text.substring(0, 150),
            snippets: [],
          });
          speakerSegments.set(label, []);
        }
        speakerMap.get(label)!.segment_count++;
        speakerSegments.get(label)!.push(segment);
      }

      // Select up to 3 representative snippets per speaker (early, mid, late)
      const MIN_SNIPPET_DURATION_MS = 2000;
      for (const [label, info] of speakerMap) {
        const segs = speakerSegments.get(label) ?? [];
        const valid = segs.filter(
          (s) =>
            s.start_ms != null &&
            s.end_ms != null &&
            s.end_ms - s.start_ms >= MIN_SNIPPET_DURATION_MS,
        );

        let picked: typeof valid;
        if (valid.length <= 3) {
          picked = valid;
        } else {
          const uniqueIndices = Array.from(new Set([
            0,
            Math.floor(valid.length * 0.4),
            Math.floor(valid.length * 0.8),
          ]));
          picked = uniqueIndices.map((i) => valid[i]).filter((s): s is NonNullable<typeof s> => s != null);
        }

        info.snippets = picked.map((s) => ({
          segment_id: s.id,
          start_ms: s.start_ms!,
          end_ms: s.end_ms!,
          start_time: s.start_time,
          end_time: s.end_time,
          text: s.text,
          duration_ms: s.end_ms! - s.start_ms!,
        }));
      }

      const speakerList = Array.from(speakerMap.values());
      setSpeakers(speakerList);

      // Initialize assignments with 'unknown' for all speakers
      const initialAssignments: Record<string, SpeakerRoleValue> = {};
      speakerList.forEach((speaker) => {
        initialAssignments[speaker.speaker_label] = 'unknown';
      });

      // Load existing speaker_role_mappings (auto-detected or previously saved)
      const { data: existingRoles } = await supabase
        .from('speaker_role_mappings')
        .select('speaker_label, speaker_role')
        .eq('session_id', sessionId);

      const hasExistingRoles = existingRoles && existingRoles.length > 0;
      if (hasExistingRoles) {
        for (const r of existingRoles) {
          const role = r.speaker_role as typeof initialAssignments[string];
          if (role && initialAssignments[r.speaker_label] !== undefined) {
            initialAssignments[r.speaker_label] = role;
          }
        }
      }

      // Fallback heuristics: only run if no existing roles or all are unknown
      const allExistingAreUnknown = hasExistingRoles && existingRoles!.every(
        (r) => !r.speaker_role || r.speaker_role === 'unknown'
      );
      const needsAutoDetect = !hasExistingRoles || allExistingAreUnknown;

      if (needsAutoDetect) {
        // Heuristic 1: Infer roles from speaker_label text (e.g., "Instructor - 2" → instructor)
        let anyInferred = false;
        for (const speaker of speakerList) {
          const inferred = inferRoleFromLabel(speaker.speaker_label);
          if (inferred) {
            initialAssignments[speaker.speaker_label] = inferred;
            anyInferred = true;
          }
        }

        // Heuristic 2: If no labels were inferred, use segment count to assign roles.
        // Standard crew configurations:
        //   2 speakers: instructor (most speech) + first_officer
        //   3 speakers: instructor (most speech) + captain (2nd) + first_officer (3rd)
        if (!anyInferred) {
          const sortedBySpeech = [...speakerList].sort((a, b) => b.segment_count - a.segment_count);

          if (sortedBySpeech.length === 2) {
            // 2 speakers: instructor + first_officer
            initialAssignments[sortedBySpeech[0]!.speaker_label] = 'instructor';
            initialAssignments[sortedBySpeech[1]!.speaker_label] = 'first_officer';
          } else if (sortedBySpeech.length === 3) {
            // 3 speakers: instructor + captain + first_officer
            initialAssignments[sortedBySpeech[0]!.speaker_label] = 'instructor';
            initialAssignments[sortedBySpeech[1]!.speaker_label] = 'captain';
            initialAssignments[sortedBySpeech[2]!.speaker_label] = 'first_officer';
          } else if (sortedBySpeech.length >= 4) {
            // 4+ speakers: instructor + first_officer + first_officer_2 + observer/unknown
            initialAssignments[sortedBySpeech[0]!.speaker_label] = 'instructor';
            initialAssignments[sortedBySpeech[1]!.speaker_label] = 'first_officer';
            initialAssignments[sortedBySpeech[2]!.speaker_label] = 'first_officer_2';
            for (let si = 3; si < sortedBySpeech.length; si++) {
              initialAssignments[sortedBySpeech[si]!.speaker_label] = 'observer';
            }
          }
        }
      }

      setAssignments(initialAssignments);
      setLoading(false);
    } catch (err) {
      console.error('[SpeakerRoleAssignmentModal] Error loading speakers:', err);
      setError(err instanceof Error ? err.message : 'Failed to load speakers');
      setLoading(false);
    }
  };

  const handleRoleChange = (speakerLabel: string, role: SpeakerRoleValue) => {
    setAssignments((prev) => ({
      ...prev,
      [speakerLabel]: role,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      // Build assignments array
      const assignmentList: SpeakerRoleAssignment[] = speakers.map((speaker) => ({
        speaker_label: speaker.speaker_label,
        speaker_role: assignments[speaker.speaker_label] || 'unknown',
        // Optional: Link to user IDs if role matches
        user_id:
          assignments[speaker.speaker_label] === 'instructor'
            ? instructorId
            : assignments[speaker.speaker_label] === 'captain'
            ? captainId
            : assignments[speaker.speaker_label] === 'first_officer'
            ? firstOfficerId
            : undefined,
      }));

      // Get auth token
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) {
        throw new Error('Not authenticated');
      }

      // Call API endpoint
      const response = await fetch('/api/assign-speaker-roles', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sessionId,
          assignments: assignmentList,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to assign speaker roles');
      }

      const result = await response.json();
      console.log('[SpeakerRoleAssignmentModal] Assignment successful:', result);

      // Stop audio and close modal
      stop();
      onComplete(assignmentList);
      onClose();
    } catch (err) {
      console.error('[SpeakerRoleAssignmentModal] Error saving assignments:', err);
      setError(err instanceof Error ? err.message : 'Failed to save speaker roles');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    stop();
    onClose();
  };

  const getRoleBadgeColor = (role: string) => {
    const opt = ALL_ROLE_OPTIONS.find(o => o.value === role);
    return opt?.badgeColor ?? 'bg-yellow-100 text-yellow-800 border-yellow-300';
  };

  // Filter roles to show: scenario roles + other + unknown (always available)
  // When a base role is included, also include its _2 variant (dual pilot support)
  const expandedScenarioRoles = new Set(scenarioRoles);
  if (expandedScenarioRoles.has('first_officer')) expandedScenarioRoles.add('first_officer_2');
  if (expandedScenarioRoles.has('captain')) expandedScenarioRoles.add('captain_2');

  const visibleRoleOptions = expandedScenarioRoles.size > 0
    ? ALL_ROLE_OPTIONS.filter(o =>
        expandedScenarioRoles.has(o.value) || o.value === 'other' || o.value === 'unknown'
      )
    : ALL_ROLE_OPTIONS;

  const hasAtLeastOnePilot = Object.values(assignments).some((role) =>
    role === 'captain' || role === 'captain_2' || role === 'first_officer' || role === 'first_officer_2' || role === 'trainee'
  );
  const canSave = hasAtLeastOnePilot || speakers.length === 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-2 sm:mx-4 max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-3 py-2 sm:px-6 sm:py-4 border-b border-steel/20 flex-shrink-0">
          <h2 className="text-lg sm:text-xl font-bold text-navy">Assign Speaker Roles</h2>
          <p className="hidden sm:block text-sm text-steel mt-1">
            Identify which speaker is the instructor and which is the pilot(s). This ensures assessments accurately evaluate pilot performance.
          </p>
        </div>

        {/* Content */}
        <div className="px-3 py-2 sm:px-6 sm:py-4 flex-1 overflow-y-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-orca-elevate"></div>
              <p className="mt-3 text-steel">Loading speakers...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-red-800 text-sm">{error}</p>
              <button
                onClick={loadSpeakers}
                className="mt-2 px-3 py-1 bg-red-100 text-red-800 rounded hover:bg-red-200 text-sm font-medium"
              >
                Retry
              </button>
            </div>
          ) : speakers.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-steel">No speakers found in transcript</p>
            </div>
          ) : (
            <>
              {/* Warning if no pilot assigned */}
              {!hasAtLeastOnePilot && (
                <div className="mb-2 sm:mb-4 bg-yellow-50 border border-yellow-200 rounded-md p-2 sm:p-4">
                  <p className="text-yellow-800 text-sm font-medium">
                    ⚠️ At least one speaker must be assigned as "Captain", "First Officer", or "Trainee" for pilot assessments to work correctly.
                  </p>
                </div>
              )}

              {/* Speaker List */}
              <div className="space-y-2 sm:space-y-3">
                {speakers.map((speaker, speakerIndex) => {
                  const snippetIds = new Set(speaker.snippets.map((s) => s.segment_id));
                  const displayLabel = getDisplayLabel(speaker.speaker_label, speakerIndex);
                  const showOriginalLabel = displayLabel !== speaker.speaker_label;
                  return (
                  <div
                    key={speaker.speaker_label}
                    className="border border-steel/30 rounded-lg p-2 sm:p-4 hover:border-orca-elevate/50 transition-colors"
                  >
                    {/* Speaker Header */}
                    <div className="flex items-center justify-between mb-1.5 sm:mb-3">
                      <div>
                        <h3 className="font-semibold text-navy">{displayLabel}</h3>
                        <p className="text-xs text-steel">
                          {speaker.segment_count} segments • First appearance: {speaker.first_appearance}
                          {showOriginalLabel && <span className="ml-1 text-steel/60">(label: {speaker.speaker_label})</span>}
                        </p>
                      </div>
                      <span
                        className={`px-3 py-1 text-xs font-medium border rounded-full ${getRoleBadgeColor(
                          assignments[speaker.speaker_label] || 'unknown'
                        )}`}
                      >
                        {(assignments[speaker.speaker_label] || 'unknown').toUpperCase()}
                      </span>
                    </div>

                    {/* Live Transcript / Sample Text — hidden on mobile to save space */}
                    <div className="hidden sm:block">
                      <LiveTranscriptScroller
                        allSegments={allSegments}
                        speakerLabel={speaker.speaker_label}
                        playbackState={playbackState}
                        speakerSnippetIds={snippetIds}
                      />
                    </div>

                    {/* Audio Snippet Player */}
                    <AudioSnippetPlayer
                      snippets={speaker.snippets}
                      speakerLabel={speaker.speaker_label}
                      playbackState={playbackState}
                      onPlaySnippet={playSnippet}
                      onPause={pause}
                      onResume={resume}
                      onSeek={seekWithinSnippet}
                      disabled={!audioUrl}
                    />

                    {/* Role Selector — fixed grid, 2 rows */}
                    <div className="grid grid-cols-6 gap-1.5">
                      {visibleRoleOptions.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={() => handleRoleChange(speaker.speaker_label, opt.value)}
                          className={`px-1.5 py-1.5 text-[11px] font-semibold rounded-md transition-colors leading-tight text-center whitespace-nowrap ${
                            assignments[speaker.speaker_label] === opt.value
                              ? opt.activeColor
                              : 'bg-steel/10 text-steel hover:bg-steel/20'
                          }`}
                        >
                          {opt.shortLabel}
                        </button>
                      ))}
                    </div>
                  </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-3 py-2 sm:px-6 sm:py-4 border-t border-steel/20 flex justify-between items-center flex-shrink-0 gap-2">
          <button
            onClick={handleSkip}
            className="px-2 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold text-steel hover:text-navy transition-colors"
            disabled={saving}
          >
            <span className="sm:hidden">Skip</span>
            <span className="hidden sm:inline">Skip for Now</span>
          </button>
          <div className="flex space-x-1.5 sm:space-x-3">
            <button
              onClick={() => { stop(); onClose(); }}
              className="px-2 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold text-steel border border-steel/30 rounded-md hover:bg-steel/10 transition-colors"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold bg-orca-elevate text-white rounded-md hover:bg-orca-elevate/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={saving || !canSave || loading}
            >
              {saving ? (
                <>
                  <span className="inline-block animate-spin mr-2">⏳</span>
                  Saving...
                </>
              ) : (
                <><span className="sm:hidden">Assign</span><span className="hidden sm:inline">Assign Roles</span></>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
